import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";

const RESULT_CLS = {
  "Verified": "text-emerald-700",
  "Likely Verified": "text-teal-700",
  "Review Required": "text-amber-700",
  "Not Verified": "text-red-700",
  "Insufficient Data": "text-slate-500",
};

export default function AdminVerifyReport() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/verify-report", { params: { start, end } });
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const order = data?.result_order || ["Verified", "Likely Verified", "Review Required", "Not Verified", "Insufficient Data"];

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="admin-verify-report">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-emerald-600" /> Served vs. Verified — ARPS Verify Report
        </h2>
        <p className="text-slate-500">Compliance view: successful serves broken down by US Census Bureau Geocoder location-verification result. Servers with the lowest alignment surface first.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500">From</label>
          <Input type="date" data-testid="verify-report-start" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500">To</label>
          <Input type="date" data-testid="verify-report-end" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" />
        </div>
        {(start || end) && <Button variant="outline" data-testid="verify-report-clear" onClick={() => { setStart(""); setEnd(""); }}>Clear</Button>}
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : !data ? (
        <p className="text-slate-400">No report available.</p>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="verify-report-overall">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Overall — {data.total_served.toLocaleString()} served</p>
              <p className="text-sm">Verified alignment: <span className="font-bold text-emerald-700" data-testid="verify-report-verified-pct">{data.overall.verified_pct}%</span></p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-5">
              {order.map((r) => (
                <div key={r} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`overall-${r.replace(/\s+/g, "-").toLowerCase()}`}>
                  <p className={`text-xs font-semibold ${RESULT_CLS[r]}`}>{r}</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">{data.overall.counts[r]}</p>
                  <p className="text-xs text-slate-400">{data.overall.percentages[r]}%</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-6 py-4"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">By process server</p></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-400">
                    <th className="px-6 py-3">Server</th>
                    <th className="px-4 py-3 text-right">Served</th>
                    {order.map((r) => <th key={r} className="px-4 py-3 text-right">{r}</th>)}
                    <th className="px-4 py-3 text-right">Verified %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_server.map((s) => (
                    <tr key={s.server_id} className="border-t border-slate-50" data-testid={`verify-server-row-${s.server_id}`}>
                      <td className="px-6 py-3 font-medium text-slate-900">{s.server_name}</td>
                      <td className="px-4 py-3 text-right font-semibold">{s.total}</td>
                      {order.map((r) => <td key={r} className="px-4 py-3 text-right text-slate-600">{s.counts[r]}<span className="ml-1 text-xs text-slate-400">({s.percentages[r]}%)</span></td>)}
                      <td className={`px-4 py-3 text-right font-bold ${s.verified_pct < 60 ? "text-red-600" : s.verified_pct < 85 ? "text-amber-600" : "text-emerald-700"}`}>{s.verified_pct}%</td>
                    </tr>
                  ))}
                  {data.by_server.length === 0 && <tr><td colSpan={order.length + 3} className="px-6 py-12 text-center text-slate-400">No served attempts in this range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
